/**
 * SubdomainPrompt — renders the cross-domain "enter your company subdomain"
 * input block, including:
 *   - input field with live-validation status (via useSubdomainValidation state)
 *   - base-domain suffix hint (e.g., ".plannerpuffin.com")
 *   - "Find my company" link trigger
 *   - optional welcome-back banner when a stored subdomain is detected
 *
 * Stateless shell component — caller owns `subdomain`, `onSubdomainChange`,
 * `onLookupOpen`, and optionally `validation` + `welcomeBack`.
 *
 * @module @puffin-core/login-shell/components/SubdomainPrompt
 */

import PropTypes from 'prop-types';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';

export function SubdomainPrompt({
  subdomain,
  onSubdomainChange,
  baseDomain = '',
  onLookupOpen,
  validation,
  welcomeBack,
  disabled = false,
  t,
}) {
  const translate = useLoginTranslate(t);
  const inputId = 'subdomain-input';
  const errorId = 'subdomain-input-error';
  const hasError = Boolean(validation?.error);

  // The banner and the three validation status lines are resolved into named
  // slots above the return. `&&` chains are kept verbatim — no `!!` coercion and
  // no early return, so render semantics are unchanged.
  const welcomeBackBanner = welcomeBack && (
    <div className="welcome-back-banner" data-testid="welcome-back-banner" role="status">
      <p className="text text-sm">
        {translate('subdomain.welcomeBack').replace('{subdomain}', welcomeBack.subdomain || '')}
      </p>
      {welcomeBack.onClear && (
        <button
          type="button"
          className="btn btn-link text-sm"
          onClick={welcomeBack.onClear}
          data-testid="welcome-back-clear"
        >
          {translate('subdomain.useDifferentCompany')}
        </button>
      )}
    </div>
  );

  const validatingStatus = validation?.isValidating && (
    <p className="text text-sm text-muted" role="status" data-testid="subdomain-validating">
      {translate('subdomain.validating')}
    </p>
  );

  const validCompanyStatus = validation?.isValid && validation?.companyName && (
    <p className="text text-sm text-success" role="status" data-testid="subdomain-valid-company">
      {translate('subdomain.foundCompany').replace('{companyName}', validation.companyName)}
    </p>
  );

  const errorStatus = hasError && (
    <p id={errorId} className="form-error" role="alert">
      {validation.error}
    </p>
  );

  return (
    <div className="subdomain-prompt" data-testid="subdomain-prompt">
      {welcomeBackBanner}

      <div className="form-group">
        <label htmlFor={inputId} className="form-label">
          {translate('subdomain.label')}
        </label>
        <div className="input-group">
          <input
            id={inputId}
            className={`form-input${hasError ? ' form-input-error' : ''}`}
            type="text"
            value={subdomain}
            onChange={(e) => onSubdomainChange(e.target.value)}
            disabled={disabled}
            autoComplete="off"
            aria-invalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
            data-testid="subdomain-input"
          />
          {baseDomain && (
            <span className="input-group-addon" data-testid="subdomain-suffix">
              {baseDomain}
            </span>
          )}
        </div>
        {validatingStatus}
        {validCompanyStatus}
        {errorStatus}
      </div>

      {onLookupOpen && (
        <p className="text text-sm text-muted">
          {translate('subdomain.cantFind')}{' '}
          <button
            type="button"
            className="btn btn-link text-sm"
            onClick={onLookupOpen}
            data-testid="subdomain-lookup-trigger"
          >
            {translate('subdomain.lookupAction')}
          </button>
        </p>
      )}
    </div>
  );
}

SubdomainPrompt.propTypes = {
  subdomain: PropTypes.string.isRequired,
  onSubdomainChange: PropTypes.func.isRequired,
  baseDomain: PropTypes.string,
  onLookupOpen: PropTypes.func,
  validation: PropTypes.shape({
    isValid: PropTypes.bool,
    isValidating: PropTypes.bool,
    companyName: PropTypes.string,
    error: PropTypes.string,
  }),
  welcomeBack: PropTypes.shape({
    subdomain: PropTypes.string,
    onClear: PropTypes.func,
  }),
  disabled: PropTypes.bool,
  t: PropTypes.func,
};

export default SubdomainPrompt;
